// REFACTORED: 2026-02-06
// FROM src/components/teamInsights/MyTeamPage.tsx

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Alert,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Context
import { useAuth } from '../contexts/AuthContext';

// API Service
import { dashboardApi, getTableSyncStatus } from '../services/api';
import LastSyncedLabel from '../components/common/LastSyncedLabel';

// Types
import { FilterRequest, MyInsightsResponse } from '../types/api';

// Utilities
import { getDefaultMyTeamFilterRequest } from '../utils/DateUtils';
import { hasFiltersChanged } from '../utils/FilterUtils';

// Lazy components (load after data)
import {
  LazyCertificationsTrendChart,
  LazyTier1DRIProfilesTrendChart,
  LazyNewCompetenciesTrendChart,
  LazyTeamScoreCards,
  LazyTeamHeadcountMetrics,
  LazyTeamUtilizationAndProjects,
  LazyTeamDriCertsSkillsTabbedSection,
  LazyTeamInsightsTabbedSection,
} from '../components/LazyComponents';
import MyTeamTopFilters from '../components/filters/page/MyTeamTopFilters';

// Theme
import { SEARCE_COLORS } from '../theme';
import { MetricCardsRowSkeleton } from '../components/common/loading';
import { CLIENT_SUCCESS_REGIONAL_MANAGER_ROLE } from '../constants/BusinessConstants';
import { normalizeEmail } from '../components/customerSentiment/forms/emailRecipients';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MyTeamPage: React.FC = () => {
  const { user } = useAuth();

  // Fetch user profile from happier data to get actual BU (source of truth)
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile-happier', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      return await dashboardApi.getUserProfile();
    },
    enabled: !!user?.email,
  });

  const isClientSuccess = useMemo(() => {
    // Use BU from happier data (source of truth) instead of assigned BUs
    return userProfile?.business_unit === 'Client Success';// || userProfile?.user_email === 'ananthapadmanabhan.a@searce.com' || userProfile?.user_email === 'dhruvi.sharma@searce.com';
  }, [userProfile]);

  const showCsmAnomaliesTab = user?.role?.name === CLIENT_SUCCESS_REGIONAL_MANAGER_ROLE;

  const { data: syncData, isLoading: syncLoading } = useQuery({
    queryKey: ['sync-status'],
    queryFn: getTableSyncStatus,
    staleTime: 10 * 60 * 1000,
    enabled: !!user?.email,
  });

  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MyInsightsResponse | null>(null);

  const [filters, setFilters] = useState<FilterRequest>(() => getDefaultMyTeamFilterRequest());
  const [activeFilters, setActiveFilters] = useState<FilterRequest>(() => getDefaultMyTeamFilterRequest());

  // Single source: team-insights/my-team-members (team_emails, managers, team_member_names)
  const { data: teamInsightsMembers, isLoading: teamMembersLoading } = useQuery({
    queryKey: ['my-team-members', user?.email],
    queryFn: () => dashboardApi.getTeamInsightsMyTeamMembers(),
    enabled: !!user?.email,
    staleTime: 5 * 60 * 1000,
  });

  const teamMemberEmails = teamInsightsMembers?.team_emails ?? [];
  const teamMemberNames = teamInsightsMembers?.team_member_names ?? [];
  const teamMemberDetails = teamInsightsMembers?.team_member_details ?? [];
  const availableManagers = teamInsightsMembers?.managers ?? [];
  // For filter dropdown: use names for display and API employee_names.
  // Scoped to the selected Employment Status so inactive people don't show up under "Active Only".
  const teamMembersForFilter: string[] = useMemo(() => {
    const status = filters.employee_status || 'ACTIVE';
    if (status === 'BOTH' || teamMemberDetails.length === 0) return teamMemberNames;
    return teamMemberDetails
      .filter((m) => (m.status || '').toUpperCase() === status.toUpperCase())
      .map((m) => m.name);
  }, [teamMemberDetails, teamMemberNames, filters.employee_status]);

  // Fetch available SBUs for the filter
  const { data: availableSbus = [] } = useQuery({
    queryKey: ['my-team-sbus', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      return await dashboardApi.getMyTeamSbus();
    },
    enabled: !!user?.email,
    staleTime: 5 * 60 * 1000,
  });


  // Fetch DRI details for the DRI Details table
  const managersKey = React.useMemo(() => {
    return activeFilters.managers && activeFilters.managers.length > 0
      ? JSON.stringify([...activeFilters.managers].sort())
      : 'all';
  }, [activeFilters.managers]);

  const durationKey = React.useMemo(() => {
    return activeFilters.start_year && activeFilters.start_month && activeFilters.end_year && activeFilters.end_month
      ? `${activeFilters.start_year}-${activeFilters.start_month}-${activeFilters.end_year}-${activeFilters.end_month}`
      : 'all';
  }, [activeFilters.start_year, activeFilters.start_month, activeFilters.end_year, activeFilters.end_month]);

  const employeeNamesKey = React.useMemo(() => {
    return activeFilters.employee_names && activeFilters.employee_names.length > 0
      ? JSON.stringify([...activeFilters.employee_names].sort())
      : 'all';
  }, [activeFilters.employee_names]);

  const projectCodesKey = React.useMemo(() => {
    return activeFilters.project_codes && activeFilters.project_codes.length > 0
      ? JSON.stringify([...activeFilters.project_codes].sort())
      : 'all';
  }, [activeFilters.project_codes]);

  const sbuKey = React.useMemo(() => {
    return activeFilters.sub_bus && activeFilters.sub_bus.length > 0
      ? JSON.stringify([...activeFilters.sub_bus].sort())
      : 'all';
  }, [activeFilters.sub_bus]);

  const { data: driDetails = [], isLoading: driLoading = false, error: driError } = useQuery({
    queryKey: ['my-team-dri-details', user?.email, durationKey, managersKey, employeeNamesKey, sbuKey, activeFilters.employee_status || 'ACTIVE'],
    queryFn: async ({ queryKey }) => {
      if (!user?.email) {
        return [];
      }

      const managers = managersKey === 'all' ? [] : JSON.parse(managersKey);
      const employee_names = employeeNamesKey === 'all' ? [] : JSON.parse(employeeNamesKey);
      const sub_bus = sbuKey === 'all' ? [] : JSON.parse(sbuKey);

      const apiFilters: any = {
        start_year: activeFilters.start_year,
        start_month: activeFilters.start_month,
        end_year: activeFilters.end_year,
        end_month: activeFilters.end_month,
        employee_status: activeFilters.employee_status || 'ACTIVE'
      };
      if (employee_names.length > 0) apiFilters.employee_names = employee_names;
      if (managers.length > 0) apiFilters.managers = managers;
      if (sub_bus.length > 0) apiFilters.sub_bus = sub_bus;

      return await dashboardApi.getMyTeamDriDetails(apiFilters);
    },
    enabled: !!user?.email,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Filtered DRI details from Team Insights table
  const [filteredDriDetailsFromTable, setFilteredDriDetailsFromTable] = useState<any[]>([]);

  // Calculate headcount metrics
  const headcountMetrics = React.useMemo(() => {
    if (!filteredDriDetailsFromTable || filteredDriDetailsFromTable.length === 0) {
      return null;
    }
    const activeHeadcount = filteredDriDetailsFromTable.filter((d: any) => d.current_status === 'ACTIVE').length;
    const allocatedHeadcount = filteredDriDetailsFromTable.filter((d: any) => d.allocation_status === 'Allocated' && d.current_status === 'ACTIVE').length;
    const billableHeadcount = filteredDriDetailsFromTable.filter((d: any) =>
      d.allocation_status === 'Allocated' && d.billable_status === 'Billable' && d.current_status === 'ACTIVE'
    ).length;
    const internalHeadcount = filteredDriDetailsFromTable.filter((d: any) =>
      d.allocation_status === 'Allocated' && d.current_status === 'ACTIVE' && d.internal_status === 'Allocated'
    ).length;
    const benchStrength = Math.max(0, activeHeadcount - allocatedHeadcount);
    const totalUtilization = activeHeadcount > 0 ? (allocatedHeadcount / activeHeadcount * 100) : 0;
    return {
      activeHeadcount,
      allocatedHeadcount,
      billableHeadcount,
      internalHeadcount,
      benchStrength,
      totalUtilization,
    };
  }, [filteredDriDetailsFromTable]);

  // Fetch user insights data
  const { data: insightsData, isLoading, error: queryError } = useQuery({
    queryKey: ['my-team', user?.email, durationKey, projectCodesKey, managersKey, employeeNamesKey, sbuKey, activeFilters.employee_status || 'ACTIVE'],
    queryFn: async () => {
      const apiFilters: any = {
        start_year: activeFilters.start_year,
        start_month: activeFilters.start_month,
        end_year: activeFilters.end_year,
        end_month: activeFilters.end_month,
        project_codes: activeFilters.project_codes,
        employee_status: activeFilters.employee_status || 'ACTIVE'
      };
      if (activeFilters.employee_names && activeFilters.employee_names.length > 0) apiFilters.employee_names = activeFilters.employee_names;
      if (activeFilters.managers && activeFilters.managers.length > 0) apiFilters.managers = activeFilters.managers;
      if (activeFilters.sub_bus && activeFilters.sub_bus.length > 0) apiFilters.sub_bus = activeFilters.sub_bus;

      return await dashboardApi.getMyTeam(apiFilters);
    },
    enabled: !!user?.email,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const csmOwnedProjects = useMemo(() => {
    if (!showCsmAnomaliesTab || !user?.email) return [];
    const self = normalizeEmail(user.email);
    return (insightsData?.projects ?? []).filter(
      (p) => normalizeEmail(String(p.project_owner_email ?? '')) === self
    );
  }, [showCsmAnomaliesTab, user?.email, insightsData?.projects]);

  const teamDriInsights = insightsData?.dri_insights;
  const teamDriInsightsLoading = isLoading;
  const teamDriInsightsError = queryError;

  // Fetch user resource analytics data
  const { data: resourceAnalyticsData, isLoading: resourceAnalyticsLoading, error: resourceAnalyticsError } = useQuery({
    queryKey: ['my-team-resource-analytics', user?.email, durationKey, projectCodesKey, managersKey, employeeNamesKey, sbuKey, activeFilters.employee_status || 'ACTIVE'],
    queryFn: async () => {
      const apiFilters: any = {
        start_year: activeFilters.start_year,
        start_month: activeFilters.start_month,
        end_year: activeFilters.end_year,
        end_month: activeFilters.end_month,
        project_codes: activeFilters.project_codes,
        employee_status: activeFilters.employee_status || 'ACTIVE'
      };
      if (activeFilters.employee_names && activeFilters.employee_names.length > 0) apiFilters.employee_names = activeFilters.employee_names;
      if (activeFilters.managers && activeFilters.managers.length > 0) apiFilters.managers = activeFilters.managers;
      if (activeFilters.sub_bus && activeFilters.sub_bus.length > 0) apiFilters.sub_bus = activeFilters.sub_bus;

      return await dashboardApi.getMyTeamResourceAnalytics(apiFilters);
    },
    enabled: !!user?.email,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch monthly headcount trends
  const { data: monthlyHeadcountData, isLoading: isLoadingMonthlyHeadcount } = useQuery({
    queryKey: ['my-team-monthly-headcount-trends', durationKey, employeeNamesKey, activeFilters.employee_status || 'ACTIVE', teamMemberNames?.length],
    queryFn: async () => {
      const finalEmployeeNames = activeFilters.employee_names && activeFilters.employee_names.length > 0
        ? activeFilters.employee_names
        : (teamMemberNames || []);

      const apiFilters: any = {
        start_year: activeFilters.start_year,
        start_month: activeFilters.start_month,
        end_year: activeFilters.end_year,
        end_month: activeFilters.end_month,
        employee_status: activeFilters.employee_status || 'ACTIVE',
        employee_names: finalEmployeeNames,
        sub_bus: activeFilters.sub_bus,
      };

      return await dashboardApi.getMonthlyHeadcountTrends(apiFilters);
    },
    enabled: !!user?.email && teamMemberNames && teamMemberNames.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch monthly attrition trends
  const { data: monthlyAttritionData, isLoading: isLoadingMonthlyAttrition } = useQuery({
    queryKey: ['my-team-monthly-attrition-trends', durationKey, employeeNamesKey, activeFilters.employee_status || 'ACTIVE', teamMemberNames?.length],
    queryFn: async () => {
      const finalEmployeeNames = activeFilters.employee_names && activeFilters.employee_names.length > 0
        ? activeFilters.employee_names
        : (teamMemberNames || []);

      const apiFilters: any = {
        start_year: activeFilters.start_year,
        start_month: activeFilters.start_month,
        end_year: activeFilters.end_year,
        end_month: activeFilters.end_month,
        employee_status: activeFilters.employee_status || 'ACTIVE',
        employee_names: finalEmployeeNames,
        sub_bus: activeFilters.sub_bus
      };

      return await dashboardApi.getMonthlyAttritionTrends(apiFilters);
    },
    enabled: !!user?.email && teamMemberNames && teamMemberNames.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleFiltersChange = (newFilters: FilterRequest) => {
    setFilters(newFilters);
  };

  const handleRefresh = () => {
    setActiveFilters(filters);
    queryClient.invalidateQueries({ queryKey: ['my-team', user?.email] });
  };

  const filtersHaveChanged = hasFiltersChanged(filters, activeFilters);

  // Month range for the trend charts, taken from the page's Duration filter. Tier1 DRI and
  // New Competencies keep their own month state internally (their pickers), so they need it
  // handed to them explicitly — the certifications trend already derives its data from
  // activeFilters below. Same fallbacks as that memo, so all three charts stay in step.
  const trendMonthRange = useMemo(() => {
    const now = new Date();
    return {
      start: {
        year: activeFilters.start_year || now.getFullYear(),
        month: activeFilters.start_month || now.getMonth() + 1,
      },
      end: {
        year: activeFilters.end_year || now.getFullYear(),
        month: activeFilters.end_month || now.getMonth() + 1,
      },
    };
  }, [activeFilters.start_year, activeFilters.start_month, activeFilters.end_year, activeFilters.end_month]);

  const monthlyCertificationsTrend = useMemo(() => {
    if (!data?.certifications_and_dri?.certification_details) return [];

    const certs = data.certifications_and_dri.certification_details;
    const { start_year, start_month, end_year, end_month } = activeFilters;

    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = start_year || new Date().getFullYear();
    let currentMonth = start_month || new Date().getMonth() + 1;
    const targetEndYear = end_year || new Date().getFullYear();
    const targetEndMonth = end_month || new Date().getMonth() + 1;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthAbbrs = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    while (currentYear < targetEndYear || (currentYear === targetEndYear && currentMonth <= targetEndMonth)) {
      months.push({
        month: monthNames[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${monthAbbrs[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return months.map(monthData => {
      const targetMonthIndex = monthNames.indexOf(monthData.month) + 1;
      const count = certs.filter(cert => {
        if (!cert.issue_date) return false;
        const issueDate = new Date(cert.issue_date);
        return issueDate.getFullYear() === monthData.year && (issueDate.getMonth() + 1) === targetMonthIndex;
      }).length;
      return { ...monthData, count };
    });
  }, [data?.certifications_and_dri?.certification_details, activeFilters]);

  useEffect(() => {
    if (insightsData) {
      setData(insightsData);
      setLoading(false);
    } else if (queryError) {
      setError('Failed to load insights data');
      setLoading(false);
    }
  }, [insightsData, queryError]);

  const uniqueProjects = useMemo(() => {
    const projects = insightsData?.projects || [];
    const seen = new Set<string>();
    return projects.filter((project) => {
      const projectCode = project.project_code;
      if (!projectCode || seen.has(projectCode)) return false;
      seen.add(projectCode);
      return true;
    });
  }, [insightsData?.projects]);

  const showScoreCardsSkeleton =
    !!user?.email &&
    queryError == null &&
    (isLoading || driLoading || resourceAnalyticsLoading);

  return (
    <Box sx={{ p: 2, backgroundColor: SEARCE_COLORS.background, minHeight: '100vh' }}>
      <MyTeamTopFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onRefresh={handleRefresh}
        hasUnsavedChanges={filtersHaveChanged}
        availableProjects={uniqueProjects}
        availableEmployees={teamMembersForFilter}
        availableManagers={availableManagers}
        availableSbus={availableSbus}
        showEmployeeFilter={true}
        showSbuFilter={true}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <LastSyncedLabel isoTimestamp={syncData?.team_insights} isLoading={syncLoading} />
      </Box>

      {showScoreCardsSkeleton && (
        <Box sx={{ mb: 4, mt: -3, pt: 4, width: '100%' }}>
          <MetricCardsRowSkeleton count={8} height={88} borderRadius={18} gap={1.5} />
        </Box>
      )}
      {!showScoreCardsSkeleton && insightsData != null && (
        <LazyTeamScoreCards
          data={(data ?? insightsData) ?? ({} as MyInsightsResponse)}
          driDetails={driDetails}
          headcountMetrics={headcountMetrics}
          resourceAnalyticsData={resourceAnalyticsData}
          teamDriInsights={teamDriInsights}
        />
      )}

      {headcountMetrics != null && (
        <LazyTeamHeadcountMetrics
          headcountMetrics={headcountMetrics}
          monthlyHeadcountData={monthlyHeadcountData}
          isLoadingMonthlyHeadcount={isLoadingMonthlyHeadcount}
          monthlyAttritionData={monthlyAttritionData}
          isLoadingMonthlyAttrition={isLoadingMonthlyAttrition}
          teamMemberEmails={teamMemberEmails}
        />
      )}

      <LazyTeamUtilizationAndProjects
        resourceAnalyticsLoading={resourceAnalyticsLoading}
        resourceAnalyticsError={resourceAnalyticsError}
        resourceAnalyticsData={resourceAnalyticsData}
        activeFilters={activeFilters}
        teamMemberEmails={teamMemberEmails}
        insightsData={insightsData ?? null}
        isLoading={isLoading}
      />

      

      {insightsData != null && (
        <>
          {(data ?? insightsData)?.certifications_and_dri != null && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ height: 500, width: '100%' }}>
                  <LazyCertificationsTrendChart data={monthlyCertificationsTrend} />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ height: 500, width: '100%' }}>
                  <LazyTier1DRIProfilesTrendChart
                    filters={activeFilters}
                    dataSource="my-team"
                    defaultStartMonth={trendMonthRange.start}
                    defaultEndMonth={trendMonthRange.end}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ height: 500, width: '100%' }}>
                  <LazyNewCompetenciesTrendChart
                    filters={activeFilters}
                    dataSource="my-team"
                    defaultStartMonth={trendMonthRange.start}
                    defaultEndMonth={trendMonthRange.end}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
          <LazyTeamDriCertsSkillsTabbedSection
            driSection={{
              teamDriInsights: teamDriInsights ?? undefined,
              teamDriInsightsError: teamDriInsightsError,
              teamDriInsightsLoading: teamDriInsightsLoading,
              teamMemberEmails: teamMemberEmails,
            }}
            certificationsAndDri={(data ?? insightsData)?.certifications_and_dri ?? null}
          />
          <LazyTeamInsightsTabbedSection
            teamTable={{
              driDetails: driDetails,
              driLoading: driLoading,
              driError: driError,
              onFilteredDataChange: setFilteredDriDetailsFromTable,
              activeFilters: activeFilters,
            }}
            projectTable={{
              projects: insightsData?.projects ?? [],
              isClientSuccess: isClientSuccess,
            }}
            showCsmAnomaliesTab={showCsmAnomaliesTab}
            csmAnomalyProjects={csmOwnedProjects}
          />
        </>
      )}

      {!isLoading && insightsData == null && queryError != null && (
        <Alert severity="error" sx={{ mt: 2 }}>{error ?? 'Failed to load team insights.'}</Alert>
      )}
      {!isLoading && insightsData == null && queryError == null && !!user?.email && (
        <Box sx={{ mb: 4, mt: 2, width: '100%' }} role="status" aria-busy aria-label="Loading team overview">
          <MetricCardsRowSkeleton count={8} height={88} borderRadius={18} gap={1.5} />
        </Box>
      )}
    </Box>
  );
};

export default MyTeamPage;
